/**
 * Form Schema Registry — Phase 2a
 *
 * Typed registry of the top 8 forms the Copilot can fill.
 * `formId` matches the `data-copilot-form` attribute on the <form> element.
 * `route` is the canonical SPA route where the form lives.
 * Field names match the React Hook Form (RHF) field names used in each form.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FieldSchema {
  /** RHF field name (dot-notation for nested, e.g. "address.city") */
  name: string
  /** French label shown to the user in Copilot preview */
  label: string
  type: 'text' | 'number' | 'select' | 'switch' | 'date' | 'textarea' | 'tags' | 'multiselect'
  options?: { value: string; label: string }[]
  required?: boolean
  placeholder?: string
  /** Hint for the LLM when generating fill values */
  copilotHint?: string
  /** Inclusive minimum for `number` fields — used to clamp + default AI values. */
  min?: number
  /** Inclusive maximum for `number` fields — used to clamp AI values. */
  max?: number
}

export interface FormSchema {
  /** Matches data-copilot-form="{formId}" on the <form> element */
  formId: string
  /** Canonical SPA route where this form lives */
  route: string
  /** French display title used in Copilot UI */
  title: string
  fields: FieldSchema[]
}

// ── Registry ──────────────────────────────────────────────────────────────────

const FORM_SCHEMAS: FormSchema[] = [
  // ── 1. Risk Create ───────────────────────────────────────────────────────
  {
    formId: 'risk-create',
    route: '/risk-pro/register',
    title: 'Nouveau risque',
    fields: [
      {
        name: 'title',
        label: 'Titre du risque',
        type: 'text',
        required: true,
        placeholder: 'Ex: Biais algorithmique dans le modèle de scoring',
        copilotHint: 'Titre concis décrivant la nature du risque (min 5 chars)',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        placeholder: 'Décrivez le risque en détail, incluant le contexte...',
        copilotHint: 'Description détaillée du risque (min 20 chars)',
      },
      {
        name: 'category',
        label: 'Catégorie',
        type: 'select',
        required: true,
        options: [
          { value: 'IA', label: 'Intelligence Artificielle' },
          { value: 'Securite', label: 'Sécurité' },
          { value: 'Conformite', label: 'Conformité' },
          { value: 'Operationnel', label: 'Opérationnel' },
          { value: 'Donnees', label: 'Données' },
          { value: 'Strategique', label: 'Stratégique' },
        ],
      },
      {
        name: 'probability',
        label: 'Probabilité (1-5)',
        type: 'number',
        required: true,
        min: 1,
        max: 5,
        copilotHint: 'Entier OBLIGATOIRE de 1 (Rare) à 5 (Quasi certain) — ne jamais laisser vide',
      },
      {
        name: 'impact',
        label: 'Impact (1-5)',
        type: 'number',
        required: true,
        min: 1,
        max: 5,
        copilotHint: 'Entier OBLIGATOIRE de 1 (Négligeable) à 5 (Critique) — ne jamais laisser vide',
      },
      {
        name: 'ownerId',
        label: 'Responsable',
        type: 'text',
        copilotHint: 'UUID de l\'utilisateur responsable',
      },
      {
        name: 'dueDate',
        label: 'Date de revue',
        type: 'date',
        placeholder: 'YYYY-MM-DD',
      },
      {
        name: 'tags',
        label: 'Tags',
        type: 'tags',
        copilotHint: 'Liste de tags sous forme de tableau de chaînes',
      },
    ],
  },

  // ── 2. Incident Create ───────────────────────────────────────────────────
  {
    formId: 'incident-create',
    route: '/security-pro/incidents',
    title: 'Nouvel incident de sécurité',
    fields: [
      {
        name: 'title',
        label: 'Titre de l\'incident',
        type: 'text',
        required: true,
        placeholder: 'Ex: Tentative d\'intrusion sur le serveur API',
      },
      {
        name: 'severity',
        label: 'Sévérité',
        type: 'select',
        required: true,
        options: [
          { value: 'low', label: 'Faible' },
          { value: 'medium', label: 'Modéré' },
          { value: 'high', label: 'Élevé' },
          { value: 'critical', label: 'Critique' },
        ],
        copilotHint: 'low | medium | high | critical',
      },
      {
        name: 'incident_type',
        label: 'Type d\'incident',
        type: 'text',
        placeholder: 'Ex: Intrusion, Phishing, Fuite de données',
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: [
          { value: 'open', label: 'Déclaré' },
          { value: 'investigation', label: 'En cours d\'analyse' },
          { value: 'contained', label: 'Contenu' },
          { value: 'remediated', label: 'Remédié' },
          { value: 'closed', label: 'Clos' },
        ],
        copilotHint: 'open | investigation | contained | remediated | closed',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Décrivez l\'incident, les systèmes affectés et les actions immédiates...',
      },
    ],
  },

  // ── 3. PIA Create ────────────────────────────────────────────────────────
  {
    formId: 'pia-create',
    route: '/compliance-pro/pia',
    title: 'Nouvelle PIA',
    fields: [
      {
        name: 'projectName',
        label: 'Nom du projet',
        type: 'text',
        required: true,
        placeholder: 'Ex: Système de scoring crédit IA',
        copilotHint: 'Nom du projet ou traitement de données concerné (min 2 chars)',
      },
      {
        name: 'projectDescription',
        label: 'Description du projet',
        type: 'textarea',
        placeholder: 'Décrivez le traitement de données...',
      },
      {
        name: 'dataSubjects',
        label: 'Personnes concernées',
        type: 'multiselect',
        options: [
          { value: 'Employes', label: 'Employés' },
          { value: 'Clients', label: 'Clients' },
          { value: 'Prospects', label: 'Prospects' },
          { value: 'Fournisseurs', label: 'Fournisseurs' },
          { value: 'Partenaires', label: 'Partenaires' },
          { value: 'Mineurs', label: 'Mineurs' },
          { value: 'Patients', label: 'Patients' },
          { value: 'Citoyens', label: 'Citoyens' },
        ],
        copilotHint: 'Tableau de catégories de personnes concernées',
      },
      {
        name: 'legalBasis',
        label: 'Base légale',
        type: 'select',
        options: [
          { value: 'CONSENT', label: 'Consentement' },
          { value: 'CONTRACT', label: 'Exécution d\'un contrat' },
          { value: 'LEGAL_OBLIGATION', label: 'Obligation légale' },
          { value: 'VITAL_INTERESTS', label: 'Intérêts vitaux' },
          { value: 'PUBLIC_TASK', label: 'Mission d\'intérêt public' },
          { value: 'LEGITIMATE_INTERESTS', label: 'Intérêts légitimes' },
        ],
        copilotHint: 'Base légale du traitement RGPD/Loi 25',
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: [
          { value: 'DRAFT', label: 'Brouillon' },
          { value: 'IN_PROGRESS', label: 'En cours' },
          { value: 'REVIEW', label: 'En révision' },
          { value: 'APPROVED', label: 'Approuvé' },
        ],
        copilotHint: 'DRAFT | IN_PROGRESS | REVIEW | APPROVED',
      },
    ],
  },

  // ── 4. Finding Create ────────────────────────────────────────────────────
  {
    formId: 'finding-create',
    route: '/audit-pro/findings',
    title: 'Nouvelle constatation d\'audit',
    fields: [
      {
        name: 'title',
        label: 'Titre de la constatation',
        type: 'text',
        required: true,
        placeholder: 'Ex: Absence de politique de mot de passe formalisée',
      },
      {
        name: 'severity',
        label: 'Sévérité',
        type: 'select',
        required: true,
        options: [
          { value: 'low', label: 'Faible' },
          { value: 'medium', label: 'Modéré' },
          { value: 'high', label: 'Élevé' },
          { value: 'critical', label: 'Critique' },
        ],
        copilotHint: 'low | medium | high | critical',
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: [
          { value: 'open', label: 'Ouvert' },
          { value: 'in_progress', label: 'En cours' },
          { value: 'resolved', label: 'Résolu' },
          { value: 'closed', label: 'Clos' },
        ],
        copilotHint: 'open | in_progress | resolved | closed',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        placeholder: 'Décrivez la constatation en détail...',
        copilotHint: 'Description détaillée (min 20 chars)',
      },
      {
        name: 'recommendation',
        label: 'Recommandation',
        type: 'textarea',
        placeholder: 'Action corrective recommandée...',
      },
      {
        name: 'auditId',
        label: 'Référence audit',
        type: 'text',
        required: true,
        copilotHint: 'UUID de l\'audit ou engagement associé',
      },
    ],
  },

  // ── 5. Business Case Create ──────────────────────────────────────────────
  {
    formId: 'business-case-create',
    route: '/strategy-pro/business-case',
    title: 'Nouveau business case',
    fields: [
      {
        name: 'title',
        label: 'Titre',
        type: 'text',
        required: true,
        placeholder: 'Ex: Déploiement IA de scoring crédit',
      },
      {
        name: 'executiveSummary',
        label: 'Résumé exécutif',
        type: 'textarea',
        required: true,
        placeholder: 'Résumé des points clés...',
        copilotHint: 'Résumé pour le comité de direction (min 20 chars)',
      },
      {
        name: 'problemStatement',
        label: 'Énoncé du problème',
        type: 'textarea',
        required: true,
        placeholder: 'Décrivez le problème à résoudre...',
        copilotHint: 'Description du problème actuel (min 20 chars)',
      },
      {
        name: 'proposedSolution',
        label: 'Solution proposée',
        type: 'textarea',
        required: true,
        placeholder: 'Décrivez la solution proposée...',
        copilotHint: 'Description de la solution (min 20 chars)',
      },
      {
        name: 'currentSituation',
        label: 'Situation actuelle',
        type: 'textarea',
        required: true,
        placeholder: 'Situation actuelle et contexte...',
      },
      {
        name: 'impactOfInaction',
        label: 'Impact de l\'inaction',
        type: 'textarea',
        required: true,
        placeholder: 'Conséquences si rien n\'est fait...',
      },
      {
        name: 'whyThisSolution',
        label: 'Justification de la solution',
        type: 'textarea',
        required: true,
        placeholder: 'Pourquoi cette solution est la meilleure...',
      },
    ],
  },

  // ── 6. Assessment Create ─────────────────────────────────────────────────
  {
    formId: 'assessment-create',
    route: '/assessments/create',
    title: 'Nouvelle évaluation',
    fields: [
      {
        name: 'name',
        label: 'Nom de l\'évaluation',
        type: 'text',
        required: true,
        placeholder: 'Ex: Évaluation IA Production Q1 2024',
        copilotHint: 'Nom descriptif de l\'évaluation (min 3 chars)',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Décrivez l\'objectif et le contexte de cette évaluation...',
      },
      {
        name: 'frameworkIds',
        label: 'Frameworks',
        type: 'multiselect',
        required: true,
        options: [
          { value: 'iso-42001', label: 'ISO 42001' },
          { value: 'nist-ai-rmf', label: 'NIST AI RMF' },
          { value: 'loi-25', label: 'Loi 25 Québec' },
          { value: 'rgpd', label: 'RGPD' },
          { value: 'iso-27001', label: 'ISO 27001' },
          { value: 'soc2', label: 'SOC 2' },
        ],
        copilotHint: 'Tableau d\'identifiants de frameworks (au moins 1 requis)',
      },
      {
        name: 'scopeType',
        label: 'Type de périmètre',
        type: 'select',
        required: true,
        options: [
          { value: 'system', label: 'Système' },
          { value: 'process', label: 'Processus' },
          { value: 'organization', label: 'Organisation' },
          { value: 'project', label: 'Projet' },
        ],
        copilotHint: 'system | process | organization | project',
      },
      {
        name: 'scopeName',
        label: 'Nom du périmètre',
        type: 'text',
        required: true,
        placeholder: 'Ex: Système de recommandation IA',
        copilotHint: 'Nom du système, processus ou périmètre évalué (min 2 chars)',
      },
      {
        name: 'scheduleType',
        label: 'Type de planification',
        type: 'select',
        options: [
          { value: 'immediate', label: 'Immédiat' },
          { value: 'scheduled', label: 'Planifié' },
          { value: 'recurring', label: 'Récurrent' },
        ],
        copilotHint: 'immediate | scheduled | recurring',
      },
      {
        name: 'dueDate',
        label: 'Date d\'échéance',
        type: 'date',
        placeholder: 'YYYY-MM-DD',
      },
    ],
  },

  // ── 7. Organization Settings ─────────────────────────────────────────────
  {
    formId: 'organization-settings',
    route: '/settings/organization',
    title: 'Paramètres organisation',
    fields: [
      {
        name: 'name',
        label: 'Nom de l\'organisation',
        type: 'text',
        required: true,
        placeholder: 'Ex: ACME Corporation',
      },
      {
        name: 'code',
        label: 'Code organisation',
        type: 'text',
        required: true,
        placeholder: 'Ex: ACME-CORP',
        copilotHint: 'Code alphanumérique majuscule (lettres, chiffres, tirets, underscore)',
      },
      {
        name: 'type',
        label: 'Type d\'entité',
        type: 'select',
        required: true,
        options: [
          { value: 'company', label: 'Entreprise' },
          { value: 'division', label: 'Division' },
          { value: 'department', label: 'Département' },
          { value: 'team', label: 'Équipe' },
          { value: 'business_unit', label: 'Unité d\'affaires' },
          { value: 'cost_center', label: 'Centre de coûts' },
          { value: 'legal_entity', label: 'Entité légale' },
          { value: 'branch', label: 'Succursale' },
        ],
        copilotHint: 'Type légal ou organisationnel de l\'entité',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'text',
        placeholder: 'contact@organisation.com',
      },
      {
        name: 'phone',
        label: 'Téléphone',
        type: 'text',
        placeholder: '+1 514 000 0000',
      },
      {
        name: 'isActive',
        label: 'Entité active',
        type: 'switch',
        copilotHint: 'true | false — indique si l\'entité est opérationnelle',
      },
    ],
  },

  // ── 8. Dossier Create ────────────────────────────────────────────────────
  {
    formId: 'dossier-create',
    route: '/dossiers/new',
    title: 'Nouveau dossier',
    fields: [
      {
        name: 'title',
        label: 'Titre du dossier',
        type: 'text',
        required: true,
        placeholder: 'Ex: Audit annuel 2024 — ACME',
      },
      {
        name: 'client',
        label: 'Client',
        type: 'text',
        required: true,
        placeholder: 'Nom ou identifiant du client',
      },
      {
        name: 'modules',
        label: 'Modules',
        type: 'multiselect',
        options: [
          { value: 'M1', label: 'M1 — Évaluation Maturité' },
          { value: 'M2', label: 'M2 — Stratégie & Roadmap' },
          { value: 'M3', label: 'M3 — Gouvernance Data/IA' },
          { value: 'M4', label: 'M4 — Risques & Conformité' },
          { value: 'M5', label: 'M5 — Portfolio Use Cases IA' },
          { value: 'M6', label: 'M6 — Dashboards Executives' },
          { value: 'M7', label: 'M7 — Assistant IA RAG' },
          { value: 'M8', label: 'M8 — Conformité Avancée' },
        ],
        copilotHint: 'Tableau des codes de modules à inclure dans le dossier',
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: [
          { value: 'draft', label: 'Brouillon' },
          { value: 'active', label: 'Actif' },
          { value: 'completed', label: 'Complété' },
          { value: 'archived', label: 'Archivé' },
        ],
        copilotHint: 'draft | active | completed | archived',
      },
    ],
  },

  // ── 9. Policy Create (governance normative document) ──────────────────────
  {
    formId: 'policy-create',
    route: '/governance-pro/policies',
    title: 'Nouvelle politique',
    fields: [
      {
        name: 'title',
        label: 'Titre',
        type: 'text',
        required: true,
        placeholder: 'Ex: Politique de télétravail',
        copilotHint: 'Titre concis de la politique (min 3 chars)',
      },
      {
        name: 'domain',
        label: 'Domaine',
        type: 'select',
        options: [
          { value: 'data', label: 'Données' },
          { value: 'ai', label: 'Intelligence Artificielle' },
          { value: 'security', label: 'Sécurité' },
          { value: 'privacy', label: 'Vie privée' },
          { value: 'risk', label: 'Risque' },
          { value: 'compliance', label: 'Conformité' },
        ],
        copilotHint: 'data | ai | security | privacy | risk | compliance',
      },
      {
        name: 'owner',
        label: 'Propriétaire',
        type: 'text',
        placeholder: 'Ex: DPO, CISO, RH…',
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: [
          { value: 'draft', label: 'Brouillon' },
          { value: 'review', label: 'En révision' },
          { value: 'published', label: 'Publié' },
        ],
        copilotHint: 'draft | review | published',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Objet et portée de la politique…',
        copilotHint: 'Résumé de la politique et de son champ d’application',
      },
    ],
  },

  // ── 10. Control Create (compliance control) ───────────────────────────────
  {
    formId: 'control-create',
    route: '/compliance-pro/controls',
    title: 'Nouveau contrôle',
    fields: [
      {
        name: 'title',
        label: 'Titre',
        type: 'text',
        required: true,
        placeholder: 'Ex: Chiffrement des données au repos',
        copilotHint: 'Intitulé du contrôle de conformité (min 3 chars)',
      },
      {
        name: 'control_id',
        label: 'Identifiant',
        type: 'text',
        placeholder: 'Ex: CTL-001',
      },
      {
        name: 'category',
        label: 'Catégorie',
        type: 'text',
        placeholder: 'Ex: Sécurité, Accès, Données…',
      },
      {
        name: 'framework',
        label: 'Référentiel',
        type: 'text',
        placeholder: 'Ex: ISO 27001, SOC 2, Loi 25…',
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: [
          { value: 'draft', label: 'Brouillon' },
          { value: 'active', label: 'Actif' },
          { value: 'retired', label: 'Retiré' },
        ],
        copilotHint: 'draft | active | retired',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Objectif et modalités du contrôle…',
      },
    ],
  },

  // ── 11. Obligation Create (compliance register) ───────────────────────────
  {
    formId: 'obligation-create',
    route: '/compliance-pro/register',
    title: 'Nouvelle obligation',
    fields: [
      {
        name: 'requirement',
        label: 'Exigence',
        type: 'text',
        required: true,
        placeholder: 'Ex: Tenir un registre des traitements',
        copilotHint: 'Formulation de l’exigence réglementaire (min 3 chars)',
      },
      {
        name: 'regulation_code',
        label: 'Réglementation',
        type: 'select',
        options: [
          { value: 'RGPD', label: 'RGPD' },
          { value: 'Loi 25', label: 'Loi 25' },
          { value: 'CCPA', label: 'CCPA' },
          { value: 'SOX', label: 'SOX' },
          { value: 'PCI-DSS', label: 'PCI-DSS' },
          { value: 'ISO 27701', label: 'ISO 27701' },
        ],
        copilotHint: 'RGPD | Loi 25 | CCPA | SOX | PCI-DSS | ISO 27701',
      },
      {
        name: 'owner',
        label: 'Responsable',
        type: 'text',
        placeholder: 'Ex: DPO, Juridique…',
      },
      {
        name: 'due_date',
        label: 'Échéance',
        type: 'date',
        placeholder: 'YYYY-MM-DD',
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: [
          { value: 'Identifie', label: 'Identifié' },
          { value: 'En cours', label: 'En cours' },
          { value: 'Conforme', label: 'Conforme' },
          { value: 'Non-conforme', label: 'Non-conforme' },
        ],
        copilotHint: 'Identifie | En cours | Conforme | Non-conforme',
      },
    ],
  },

  // ── 12. Vendor Create (vendor-pro catalogue) ──────────────────────────────
  {
    formId: 'vendor-create',
    route: '/vendor-pro/catalogue',
    title: 'Nouveau fournisseur',
    fields: [
      {
        name: 'name',
        label: 'Nom du fournisseur',
        type: 'text',
        required: true,
        placeholder: 'Ex: Acme Cloud Services',
        copilotHint: 'Raison sociale du fournisseur (min 2 chars)',
      },
      {
        name: 'tier',
        label: 'Tier',
        type: 'select',
        options: [
          { value: 'Strategique', label: 'Stratégique' },
          { value: 'Critique', label: 'Critique' },
          { value: 'Important', label: 'Important' },
          { value: 'Tactical', label: 'Tactique' },
        ],
        copilotHint: 'Strategique | Critique | Important | Tactical',
      },
      {
        name: 'category',
        label: 'Catégorie',
        type: 'text',
        placeholder: 'Ex: Cloud, SaaS, Sécurité…',
      },
      {
        name: 'country',
        label: 'Pays',
        type: 'text',
        placeholder: 'Ex: CA, US, FR…',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Nature de la prestation…',
      },
    ],
  },
]

// ── Lookup helpers ────────────────────────────────────────────────────────────

/**
 * Return the schema for a given formId, or undefined if not registered.
 */
export function getFormSchema(formId: string): FormSchema | undefined {
  return FORM_SCHEMAS.find((s) => s.formId === formId)
}

/**
 * Return all registered form schemas.
 */
export function listFormSchemas(): FormSchema[] {
  return FORM_SCHEMAS
}
